import { useEffect, useMemo, useRef, useState } from 'react';
import { useIdentity } from './useIdentity';
import { usePublicIp } from './usePublicIp';
import {
  joinPresence,
  heartbeat,
  leavePresence,
  subscribePresence,
  PRESENCE_HEARTBEAT_MS,
} from '../features/chat/presence';

// UA-driven device-type guess. Mirrors the heuristic spelled out in F10.1 /
// F10.2. Computed once at module load — the navigator UA doesn't change
// during a session, so re-deriving per-render would be wasted work.
function computeDeviceType() {
  if (typeof navigator === 'undefined') return 'monitor';
  const ua = navigator.userAgent || '';
  return /Mobi|Android|iPhone/.test(ua) ? 'phone' : 'monitor';
}
const DEVICE_TYPE = computeDeviceType();

/**
 * usePresence — F4.2 lifecycle hook.
 *
 * On mount (and on partyCode change):
 *   - joinPresence with the local identity
 *   - start a 25s heartbeat interval (only while the tab is visible)
 *   - subscribe to the room's presence collection
 *
 * On unmount / partyCode change:
 *   - clear interval, leave the previous room, unsubscribe
 *
 * Visibility:
 *   - When the tab is hidden, pause the heartbeat (browser throttles fire
 *     anyway, and we'd just be wasting writes). On resume, send an immediate
 *     heartbeat so other clients see us right away.
 *
 * Returns: { members: PresenceEntry[], count: number }
 *   members are sorted by lastSeen descending.
 */
export function usePresence() {
  const { user, handle, partyCode, avatarSeed } = useIdentity();
  const { ip: publicIp } = usePublicIp();
  // CRITICAL: the presence doc is keyed by this id. It MUST be the stable
  // per-browser `user` from IdentityProvider — never the handle. Falling
  // back to handle creates an orphan presence doc keyed by the user's
  // first handle (because on a fresh visit `user` is '' for a few hundred
  // ms while the IP fetch resolves); the orphan then appears as a phantom
  // "peer" on the radar and survives until the 3-min activity window
  // ages it out. If user hasn't resolved yet, we simply don't join — the
  // join effect re-fires the moment `user` becomes truthy.
  const userId = user || '';
  // Extras object the data-layer write picks up. publicIp is included only
  // once the STUN probe resolves; null values are dropped server-side, which
  // means the field is genuinely absent in the presence doc until detection
  // succeeds. deviceType is always present.
  const extras = useMemo(
    () => ({ publicIp, deviceType: DEVICE_TYPE }),
    [publicIp],
  );

  const [members, setMembers] = useState([]);

  // Keep a ref to the most recent identity for use inside unmount cleanups
  // and interval ticks (which capture stale state otherwise). Including
  // avatarSeed here means the heartbeat picks up reroll changes without a
  // re-subscribe round-trip.
  const idRef = useRef({ userId, handle, partyCode, avatarSeed, extras });
  useEffect(() => {
    idRef.current = { userId, handle, partyCode, avatarSeed, extras };
  }, [userId, handle, partyCode, avatarSeed, extras]);

  // Track the last userId we wrote a presence doc with, scoped to the
  // current partyCode. If userId transitions (which only happens when
  // identity.user resolves after being empty — `forgetMe` is the other
  // case) we delete the previous doc here so we don't leave an orphan.
  // This is the defensive backstop; the primary fix is dropping the
  // handle fallback above so the transition rarely happens in practice.
  const lastWrittenRef = useRef({ partyCode: null, userId: null });

  // When the user rerolls their avatar, publish the new seed to their
  // presence doc immediately so the presence-stack rendering on other
  // clients (and ours) updates without waiting for the next 25s heartbeat.
  useEffect(() => {
    if (!partyCode || !userId) return;
    heartbeat(partyCode, userId, handle, avatarSeed, extras);
  }, [avatarSeed, partyCode, userId, handle, extras]);

  useEffect(() => {
    if (!partyCode || !userId) return undefined;

    // Orphan cleanup: if the previous run wrote with a different userId in
    // the same room (e.g. user transitioned from '' to a UUID), delete the
    // stale doc so it doesn't linger as a phantom peer for 3 minutes.
    const prev = lastWrittenRef.current;
    if (
      prev &&
      prev.partyCode === partyCode &&
      prev.userId &&
      prev.userId !== userId
    ) {
      leavePresence(partyCode, prev.userId);
    }
    lastWrittenRef.current = { partyCode, userId };

    let cancelled = false;
    let intervalId = null;
    let unsubscribe = () => {};

    const startInterval = () => {
      if (intervalId !== null) return;
      intervalId = setInterval(() => {
        const {
          userId: u,
          handle: h,
          partyCode: pc,
          avatarSeed: a,
          extras: ex,
        } = idRef.current;
        if (pc && u) heartbeat(pc, u, h, a, ex);
      }, PRESENCE_HEARTBEAT_MS);
    };

    const stopInterval = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.hidden) {
        stopInterval();
      } else {
        // Resume: immediate heartbeat + restart cadence.
        const {
          userId: u,
          handle: h,
          partyCode: pc,
          avatarSeed: a,
          extras: ex,
        } = idRef.current;
        if (pc && u) heartbeat(pc, u, h, a, ex);
        startInterval();
      }
    };

    const handleBeforeUnload = () => {
      const { userId: u, partyCode: pc } = idRef.current;
      if (pc && u) leavePresence(pc, u);
    };

    (async () => {
      await joinPresence(partyCode, userId, handle, avatarSeed, extras);
      if (cancelled) return;
      if (typeof document === 'undefined' || !document.hidden) {
        startInterval();
      }
    })();

    unsubscribe = subscribePresence(partyCode, (rows) => {
      // Sort newest-first; stable for identical lastSeen.
      rows.sort((a, b) => b.lastSeen - a.lastSeen);
      setMembers(rows);
    });

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibility);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      cancelled = true;
      stopInterval();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
      try {
        unsubscribe();
      } catch {
        /* tolerated */
      }
      // Only delete the row when the cleanup is firing because partyCode
      // actually changed — i.e. the user moved to a different room. If the
      // closure's partyCode still matches idRef.current.partyCode, this is
      // either a plain component unmount (e.g. closing MembersPanel while
      // the user stays in the same room via DesktopHeader / MobileNav) or
      // React StrictMode's double-invoke in dev. In both cases the user is
      // still active here, so deleting their row would make them vanish
      // from every other mounted consumer's snapshot. The 3-minute aging
      // window cleans up stale rows naturally; beforeunload handles real
      // page-close cleanup.
      if (idRef.current.partyCode !== partyCode) {
        leavePresence(partyCode, userId);
      }
      setMembers([]);
    };
    // avatarSeed is intentionally excluded from this effect's deps —
    // changing it shouldn't tear down the subscription. The dedicated
    // avatarSeed effect above pushes the new seed via heartbeat.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyCode, userId, handle]);

  return useMemo(
    () => ({ members, count: members.length }),
    [members],
  );
}

export default usePresence;
